import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { useTranslation } from "../../hooks/useTranslation";
import { TenantContext } from "../../contexts/TenantContext";
import { useBillingAccount, useBillingUsage } from "../../hooks/useApi";

const SCRIVI_A_PINGUP = "mailto:info@pingup.ai";

/**
 * In tutte le pagine, sotto la testata: le risposte del mese all'80% o finite.
 * Dice quando ripartono e cosa succede all'assistente; a chi gestisce
 * l'abbonamento porta ai piani (o a scrivere a PingUp, se il piano e' venduto
 * offline e non si compra online), un collaboratore sa che deve chiedere al
 * titolare. Sotto l'80% non disegna niente. La stessa cosa arriva per email
 * (services/avvisiRisposte.js nell'API).
 */
export default function AvvisoRisposte() {
  const { t, i18n } = useTranslation();
  const canManageBilling = Boolean(useContext(TenantContext)?.canManageBilling);
  const { data } = useBillingUsage();
  const conto = useBillingAccount({ enabled: canManageBilling });
  const offline = Boolean(conto.data?.pagamenti?.vendita_offline);

  const limite = data?.effective?.limits?.messages_per_month || 0;
  const usati = data?.usage?.messages_used || 0;
  if (!limite || usati < Math.ceil(limite * 0.8)) return null;

  const finite = usati >= limite;
  const blocco = data?.blocco_risposte === true;
  const fine = data?.usage?.messages_period_end;
  const numero = (n) => Number(n || 0).toLocaleString(i18n.language);
  const giorno = fine ? new Intl.DateTimeFormat(i18n.language, { day: "numeric", month: "long" }).format(new Date(fine)) : null;
  const conseguenza = finite
    ? blocco
      ? t("billing.answersBlockedNow")
      : t("billing.answersNotBlocked")
    : blocco
      ? t("billing.answersBlockedSoon")
      : null;
  const pulsante = finite ? "bg-red-600 hover:bg-red-700" : "bg-[#18181B] hover:bg-black";

  return (
    <div
      role={finite ? "alert" : "status"}
      // relative z-10 come la testata: sopra agli sfondi delle pagine che salgono fin qui (Home).
      className={`relative z-10 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border px-4 py-3 text-sm ${
        finite ? "border-red-200 bg-red-50 text-red-900" : "border-amber-200 bg-amber-50 text-amber-900"
      }`}
    >
      <AlertTriangle className={`h-4 w-4 shrink-0 ${finite ? "text-red-600" : "text-amber-600"}`} aria-hidden="true" />
      <p className="min-w-[12rem] flex-1">
        <span className="font-medium">
          {finite ? t("billing.answersOverTitle") : t("billing.answersAlmostTitle", { used: numero(usati), max: numero(limite) })}
        </span>{" "}
        {conseguenza ? `${conseguenza} ` : ""}
        {giorno ? t("billing.answersRestart", { date: giorno }) : ""}
        {canManageBilling ? "" : ` ${t("billing.limitAskOwner")}`}
      </p>
      {canManageBilling ? (
        offline ? (
          <a href={SCRIVI_A_PINGUP} className={`shrink-0 rounded-lg px-3 py-1.5 text-xs font-medium text-white ${pulsante}`}>
            {t("billing.answersContact")}
          </a>
        ) : (
          <Link to="/billing" className={`shrink-0 rounded-lg px-3 py-1.5 text-xs font-medium text-white ${pulsante}`}>
            {t("billing.answersUpgrade")}
          </Link>
        )
      ) : null}
    </div>
  );
}
